import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import styles from './DropDownButton.module.scss';
import Button from "../button/Button";
import {ReactComponent as ChevronDownIcon} from './ChevronDown.svg';
import PropTypes from "prop-types";
import useClickOutside from "../../hooks/useClickOutside";

const DropDownButton = ({
  label = "",
  disabled = false,
  items = [],
  type = "button",
  position = "bottom",
  size = "medium",
  variant = "primary",
  usePortal = false
}) => {
  const [showDropDown, setShowDropDown] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const containerRef = useRef(null);

  useClickOutside(containerRef, () => setShowDropDown(false));

  useEffect(() => {
    if (usePortal && showDropDown && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
        zIndex: 9999,
        minWidth: rect.width
      });
    }
  }, [showDropDown, usePortal]);

  const menuEl = showDropDown && (
    <ul
      className={`${styles.dropDownMenu} ${usePortal ? '' : styles[position] ?? ''}`}
      style={usePortal ? menuStyle : undefined}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {items.map((item, idx) => (
        <li key={idx} onClick={() => { setShowDropDown(false); item.onClick(); }}>
          {item.label}
        </li>
      ))}
    </ul>
  );

  return (
    <div className={styles.dropDownContainer} ref={containerRef}>
      {type === "button" && <Button
        label={<span className={styles.dropDownButton}>
          {label}
          <ChevronDownIcon className={`${styles.arrow} ${showDropDown ? styles.upArrow : styles.downArrow}`} />
        </span>}
        size={size}
        variant={variant}
        disabled={disabled}
        onClick={() => setShowDropDown(!showDropDown)}
      />}
      {type === "dots" && <svg 
        xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none"
        className={styles.dots}
        onClick={() => setShowDropDown(!showDropDown)}
      >
        {showDropDown && <rect width="32" height="32" rx="16" fill="#F2F2F2"/>}
        <path d="M16 17.3333C16.7364 17.3333 17.3334 16.7364 17.3334 16C17.3334 15.2636 16.7364 14.6667 16 14.6667C15.2636 14.6667 14.6667 15.2636 14.6667 16C14.6667 16.7364 15.2636 17.3333 16 17.3333Z" fill="black"/>
        <path d="M16 8.00001C16.7364 8.00001 17.3334 7.40306 17.3334 6.66668C17.3334 5.9303 16.7364 5.33334 16 5.33334C15.2636 5.33334 14.6667 5.9303 14.6667 6.66668C14.6667 7.40306 15.2636 8.00001 16 8.00001Z" fill="black"/>
        <path d="M16 26.6667C16.7364 26.6667 17.3334 26.0697 17.3334 25.3333C17.3334 24.597 16.7364 24 16 24C15.2636 24 14.6667 24.597 14.6667 25.3333C14.6667 26.0697 15.2636 26.6667 16 26.6667Z" fill="black"/>
        <path d="M16 17.3333C16.7364 17.3333 17.3334 16.7364 17.3334 16C17.3334 15.2636 16.7364 14.6667 16 14.6667C15.2636 14.6667 14.6667 15.2636 14.6667 16C14.6667 16.7364 15.2636 17.3333 16 17.3333Z" stroke="#1E1E1E" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 8.00001C16.7364 8.00001 17.3334 7.40306 17.3334 6.66668C17.3334 5.9303 16.7364 5.33334 16 5.33334C15.2636 5.33334 14.6667 5.9303 14.6667 6.66668C14.6667 7.40306 15.2636 8.00001 16 8.00001Z" stroke="#1E1E1E" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 26.6667C16.7364 26.6667 17.3334 26.0697 17.3334 25.3333C17.3334 24.597 16.7364 24 16 24C15.2636 24 14.6667 24.597 14.6667 25.3333C14.6667 26.0697 15.2636 26.6667 16 26.6667Z" stroke="#1E1E1E" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>}
      {usePortal ? ReactDOM.createPortal(menuEl, document.body) : menuEl}
    </div>
  );
};

DropDownButton.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired
    })
  ).isRequired,
  type: PropTypes.oneOf(['button', 'dots']),
  position: PropTypes.oneOf(['bottom', 'top', 'bottom-left']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.oneOf(['primary', 'secondary', 'black', 'white', 'cancel', 'grey', 'primary-light']),
  usePortal: PropTypes.bool
}

export default DropDownButton;